/**
 * ui-effects.js  (single entry point)
 * ├─ 1. revealOnScroll         –  lazy‑adds .active to .reveal
 * ├─ 2. scrollToHash           –  smooth‑scroll to anchor
 * ├─ 3. initBlazeSliders       –  sets up Blaze‑slider carousels
 * ├─ 4. loadMouseflow          –  analytics loader (multi‑project)
 * ├─ 5. hideGorgiasBranding    –  removes “Powered by” label
 * └─ 6. loadStampedWidget      –  async‑loads Stamped.io and inits it
 *
 *  No jQuery required.
 */

import addScript from '@/scripts/helpers/addScript.js';
import scrollToHash from "@/scripts/helpers/scrollToHash.js";
import makeVisible from "@/scripts/helpers/makeVisible.js";
import loaded from "@/scripts/helpers/loaded.js";
import BlazeSlider from 'blaze-slider';

(() => {
    /* ---------- Helpers ---------- */


    /* ---------- 1. Reveal on scroll ---------- */
    const initRevealOnScroll = ({
        selector = '.reveal',
        visibleClass = 'active',
        rootMargin = '0px 0px -150px 0px',
        threshold = 0.1,
    } = {}) => {
        const elements = Array.from(document.querySelectorAll(selector));
        if (!elements) {
            return;
        }

        if (!elements.length) {
            return;
        }

        if ('IntersectionObserver' in window) {
            const observer = new IntersectionObserver(
                (entries) => {
                    if (!entries) {
                        return;
                    }

                    if (!entries?.length) {
                        return;
                    }

                    entries.forEach((entry) => {
                        if (!entry?.isIntersecting) {
                            return;
                        }

                        makeVisible(entry.target, visibleClass);
                        observer.unobserve(entry.target);
                    });
                },
                {
                    rootMargin,
                    threshold
                }
            );

            elements.forEach((el) => {
                // check if already in viewport (e.g. on page load)
                if (el.getBoundingClientRect().top < window.innerHeight - 150) {
                    makeVisible(el, visibleClass);
                    return;
                }

                observer.observe(el);
            });
        } else {
            const fallback = () => {
                const winH = window.innerHeight;
                elements.forEach((el) => {
                    if (el.getBoundingClientRect().top < winH - 150) {
                        makeVisible(el, visibleClass);
                    }
                });
            };

            fallback();

            window.addEventListener('scroll', fallback, {passive: true});
            window.addEventListener('resize', fallback);
        }
    };

    /* ---------- 2. Smooth scroll to anchor ---------- */


    /* ---------- 3. Blaze‑slider init ---------- */
    const initBlazeSliders = () => {
        const sliders = document.querySelectorAll('.blaze-slider');
        if (!sliders) {
            return;
        }

        if (!sliders.length) {
            return;
        }

        const controls = document.querySelectorAll('.controls');
        sliders.forEach((sliderEl, index) => {
            const blazeSlider = new BlazeSlider(sliderEl, {
                all: {
                    enableAutoplay: false,
                    slidesToScroll: 1,
                    slidesToShow: 1,
                    transitionDuration: 300,
                    loop: true,
                },
                controls: controls[index] || undefined,
            });

            // Diagnostic; disable in production
            // blazeSlider.onSlide((page, first, last) => console.debug({ page, first, last }));
        });
    };

    /* ---------- 5. Remove Gorgias branding ---------- */
    const hideGorgiasBranding = () => {
        const initPromise = window.GorgiasChat
            ? window.GorgiasChat.init()
            : new Promise((resolve) =>
                window.addEventListener('gorgias-widget-loaded', resolve)
            );

        initPromise.then(() => {
            if (window.GorgiasChat && window.GorgiasChat.hidePoweredBy) {
                window.GorgiasChat.hidePoweredBy(true);
            }
        });
    };

    /* ---------- 6. Stamped.io loader ---------- */
    const loadStampedWidget = () => {
        const apiKey = 'pubkey-5220763I872a5qBXKL52y2byz96GuQ';
        const sId = '24693';

        addScript({
            src: 'https://cdn1.stamped.io/files/widget.min.js',
            async: true,
            onload: () => {
                if (window.StampedFn && typeof window.StampedFn.init === 'function') {
                    window.StampedFn.init({apiKey, sId});
                }
            },
        });
    };

    /* ---------- Bootstrapping ---------- */
    loaded(() => {
        initRevealOnScroll();
        scrollToHash();
        initBlazeSliders();
        hideGorgiasBranding();

        loadStampedWidget();
    });
})();
